import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Map from 'ol/Map';
import View from 'ol/View';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Style, Icon } from 'ol/style';
import { defaults as defaultControls } from 'ol/control';
import 'ol/ol.css';
import { getAddressTypeIcon } from '@/utils/deliveryAddress';
import {
  KOLN_CENTER,
  LOCAL_OSM_MAX_ZOOM,
  LOCAL_OSM_MIN_ZOOM,
  createMapTileLayers,
} from '@/components/map/BazaarakMap';

export interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address: {
    road?: string;
    house_number?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    state_district?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
}

export function formatShortAddress(result: NominatimResult): string {
  const a = result.address || {};
  const street = [a.road, a.house_number].filter(Boolean).join(' ');
  const city = a.city || a.town || a.village || '';
  const postcode = a.postcode || '';
  const cityWithCode = [postcode, city].filter(Boolean).join(' ');
  const country = a.country || '';
  return [street, cityWithCode, country].filter(Boolean).join(', ') || result.display_name;
}

function pinSvgDataUrl(remixChar: string, color = '#C9A227'): string {
  // Use a simple teardrop SVG (Remix icons aren't embeddable easily in OL without font)
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="48" viewBox="0 0 40 48">
    <path fill="${color}" d="M20 0C9 0 0 9 0 20c0 14 20 28 20 28s20-14 20-28C40 9 31 0 20 0z"/>
    <circle cx="20" cy="18" r="8" fill="#fff"/>
    <text x="20" y="22" text-anchor="middle" font-size="10" fill="${color}">${remixChar}</text>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

interface MapEditorModalProps {
  isDarkMode: boolean;
  isOpen: boolean;
  onClose: () => void;
  initialLat: string;
  initialLon: string;
  initialAddress: string;
  addressType?: number | null;
  onConfirm: (result: NominatimResult) => void;
}

async function reverseGeocode(lat: number, lon: number): Promise<NominatimResult | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${lat}&lon=${lon}`,
      { headers: { 'User-Agent': 'Bazaarak/1.0' } },
    );
    const data = await res.json();
    if (!data || !data.lat) return null;
    return {
      place_id: data.place_id || 0,
      display_name: data.display_name || '',
      lat: String(data.lat),
      lon: String(data.lon),
      address: data.address || {},
    };
  } catch {
    return null;
  }
}

export default function MapEditorModal({
  isDarkMode,
  isOpen,
  onClose,
  initialLat,
  initialLon,
  initialAddress,
  addressType = 1,
  onConfirm,
}: MapEditorModalProps) {
  const { t } = useTranslation();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapObjRef = useRef<Map | null>(null);
  const markerSourceRef = useRef<VectorSource | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<NominatimResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedResult, setSelectedResult] = useState<NominatimResult | null>(null);
  const [geocoding, setGeocoding] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const setMarker = useCallback((lat: number, lon: number) => {
    const source = markerSourceRef.current;
    if (!source) return;
    source.clear();
    const feature = new Feature({ geometry: new Point(fromLonLat([lon, lat])) });
    feature.setStyle(
      new Style({
        image: new Icon({
          src: pinSvgDataUrl('•', '#C9A227'),
          anchor: [0.5, 1],
          scale: 1,
        }),
      }),
    );
    source.addFeature(feature);
  }, []);

  const applyResult = useCallback(
    (result: NominatimResult, pan = true) => {
      setSelectedResult(result);
      setSearchQuery(formatShortAddress(result));
      setSearchResults([]);
      const lat = Number(result.lat);
      const lon = Number(result.lon);
      if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
        setMarker(lat, lon);
        if (pan && mapObjRef.current) {
          mapObjRef.current.getView().animate({ center: fromLonLat([lon, lat]), zoom: 16, duration: 350 });
        }
      }
    },
    [setMarker],
  );

  // Init / destroy map
  useEffect(() => {
    if (!isOpen || !mapRef.current) return;

    const lat = parseFloat(initialLat) || KOLN_CENTER.lat;
    const lon = parseFloat(initialLon) || KOLN_CENTER.lon;
    const markerSource = new VectorSource();
    markerSourceRef.current = markerSource;

    const map = new Map({
      target: mapRef.current,
      layers: [
        ...createMapTileLayers(),
        new VectorLayer({ source: markerSource }),
      ],
      controls: defaultControls({ zoom: true, attribution: false }),
      view: new View({
        center: fromLonLat([lon, lat]),
        zoom: 15,
        minZoom: LOCAL_OSM_MIN_ZOOM,
        maxZoom: LOCAL_OSM_MAX_ZOOM,
      }),
    });
    mapObjRef.current = map;

    const initial: NominatimResult = {
      place_id: 0,
      display_name: initialAddress || `${lat}, ${lon}`,
      lat: String(lat),
      lon: String(lon),
      address: {},
    };
    applyResult(initial, false);

    // If we only have coords, reverse-geocode for a proper address
    if (!initialAddress || !initialAddress.trim()) {
      setGeocoding(true);
      reverseGeocode(lat, lon).then((r) => {
        if (r) applyResult(r, false);
        setGeocoding(false);
      });
    }

    map.on('singleclick', async (evt) => {
      const [clickedLon, clickedLat] = toLonLat(evt.coordinate);
      setMarker(clickedLat, clickedLon);
      setGeocoding(true);
      const r = await reverseGeocode(clickedLat, clickedLon);
      if (r) {
        applyResult(r, false);
      } else {
        applyResult(
          {
            place_id: 0,
            display_name: `${clickedLat.toFixed(5)}, ${clickedLon.toFixed(5)}`,
            lat: String(clickedLat),
            lon: String(clickedLon),
            address: {},
          },
          false,
        );
      }
      setGeocoding(false);
    });

    // Force size after modal animation
    setTimeout(() => map.updateSize(), 100);
    setTimeout(() => map.updateSize(), 400);

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
      map.setTarget(undefined);
      mapObjRef.current = null;
      markerSourceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(e.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target as Node)
      ) {
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (query: string) => {
    if (query.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5`,
        { headers: { 'User-Agent': 'Bazaarak/1.0' } },
      );
      const data = await res.json();
      setSearchResults(Array.isArray(data) ? data : []);
    } catch {
      /* silent */
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchInput = (value: string) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(() => doSearch(value), 400);
  };

  const handleConfirm = () => {
    if (!selectedResult) return;
    onConfirm(selectedResult);
    onClose();
  };

  const typeIcon = getAddressTypeIcon(addressType, true);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex flex-col">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`relative z-10 flex flex-col h-full w-full overflow-hidden ${
          isDarkMode ? 'bg-dark-100' : 'bg-white'
        }`}
      >
        <div
          className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${
            isDarkMode ? 'border-dark-200/50 bg-dark-100' : 'border-stone-200/50 bg-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer ${
                isDarkMode ? 'bg-dark-200/50 text-stone-300' : 'bg-stone-100 text-stone-700'
              }`}
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-stone-100' : 'text-stone-900'}`}>
                {t('addresses.editPin')}
              </h3>
              <p className={`text-[11px] ${isDarkMode ? 'text-stone-500' : 'text-stone-400'}`}>
                {t('addresses.tapMapHint', { defaultValue: 'روی نقشه بزنید تا پین جابه‌جا شود' })}
              </p>
            </div>
          </div>
          <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center text-gold">
            <i className={`${typeIcon} text-lg`} />
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0">
          <div className="px-4 py-3 relative z-20 flex-shrink-0">
            <div
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl border-2 ${
                isDarkMode ? 'bg-dark-200/40 border-dark-200/50' : 'bg-stone-50/80 border-stone-200/50'
              }`}
            >
              <i className={`ri-search-line ${searching ? 'animate-pulse text-gold' : 'text-gold'}`} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                placeholder={t('addresses.searchPlaceholder')}
                className={`flex-1 bg-transparent outline-none text-sm ${
                  isDarkMode ? 'text-stone-100' : 'text-stone-900'
                }`}
              />
              {geocoding && <i className="ri-loader-4-line animate-spin text-gold text-sm" />}
            </div>

            <AnimatePresence>
              {searchResults.length > 0 && (
                <motion.div
                  ref={resultsRef}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className={`absolute left-4 right-4 mt-2 rounded-2xl border shadow-2xl z-30 overflow-hidden max-h-56 overflow-y-auto ${
                    isDarkMode ? 'bg-dark-100 border-dark-200/50' : 'bg-white border-stone-200/50'
                  }`}
                >
                  {searchResults.map((result) => (
                    <button
                      key={result.place_id}
                      onClick={() => applyResult(result)}
                      className={`w-full text-left px-4 py-3 flex items-start gap-3 cursor-pointer ${
                        isDarkMode ? 'hover:bg-dark-200/40' : 'hover:bg-stone-50'
                      }`}
                    >
                      <i className={`${getAddressTypeIcon(addressType)} text-gold mt-0.5`} />
                      <p className={`text-sm line-clamp-2 ${isDarkMode ? 'text-stone-200' : 'text-stone-800'}`}>
                        {result.display_name}
                      </p>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex-1 relative min-h-[240px]">
            <div ref={mapRef} className="absolute inset-0 w-full h-full" />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none z-10">
              <div
                className={`px-3 py-1.5 rounded-full text-[11px] font-medium shadow-lg ${
                  isDarkMode ? 'bg-dark-100/90 text-stone-200' : 'bg-white/95 text-stone-700'
                }`}
              >
                <i className={`${typeIcon} text-gold mr-1`} />
                {selectedResult ? formatShortAddress(selectedResult) : '…'}
              </div>
            </div>
          </div>

          <div
            className={`px-4 py-4 border-t flex-shrink-0 ${
              isDarkMode ? 'border-dark-200/50' : 'border-stone-200/50'
            }`}
          >
            <button
              onClick={handleConfirm}
              disabled={!selectedResult || geocoding}
              className="w-full py-3.5 rounded-2xl font-semibold text-white bg-gradient-to-r from-gold-dark to-gold-light disabled:opacity-50 cursor-pointer"
            >
              {t('common.confirm') || 'Confirm location'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
